/**
 * 대화에서 나온 내담자 정보를 기본 정보·초기 상담 요약에 "덧붙인다".
 * - 기존 내용은 지우거나 고치지 않는다 (빈 칸은 채우고, 내용이 있으면 새 줄로 추가)
 * - 이미 같은 내용이 있으면 추가하지 않는다 (여러 번 반영해도 중복 없음)
 * - 목록 칸은 항목 단위로 합친다
 */
import type { Client, IntakeSummary } from '../schema/v1';

export const PROFILE_FIELDS = {
  // Client — 문장 칸
  occupationRole: { target: 'client', kind: 'text', label: '직업/역할' },
  education: { target: 'client', kind: 'text', label: '학교(학점)' },
  religion: { target: 'client', kind: 'text', label: '종교' },
  substanceUse: { target: 'client', kind: 'text', label: '알코올 등 물질 관련' },
  referralSource: { target: 'client', kind: 'text', label: '의뢰 경로' },
  priorCounselingHistory: { target: 'client', kind: 'text', label: '이전 상담 경험' },
  medicalPsychiatricHistory: { target: 'client', kind: 'text', label: '의료/정신과 이력' },
  familyStructure: { target: 'client', kind: 'text', label: '가족 구조' },
  faithSpiritualContext: { target: 'client', kind: 'text', label: '신앙/영성 맥락' },
  // Client — 목록 칸
  currentMedication: { target: 'client', kind: 'list', label: '복용 약물' },
  importantRelationships: { target: 'client', kind: 'list', label: '중요한 관계' },
  majorLifeEvents: { target: 'client', kind: 'list', label: '주요 생애 사건' },
  // IntakeSummary
  presentingProblems: { target: 'intake', kind: 'list', label: '주호소' },
  onset: { target: 'intake', kind: 'text', label: '시작 시기' },
  duration: { target: 'intake', kind: 'text', label: '지속 기간' },
  functionalImpact: { target: 'intake', kind: 'text', label: '기능 손상' },
  strengths: { target: 'intake', kind: 'list', label: '강점' },
  supportResources: { target: 'intake', kind: 'list', label: '지지 자원' },
  copingResources: { target: 'intake', kind: 'list', label: '대처 자원' },
} as const;

export type ProfileField = keyof typeof PROFILE_FIELDS;
export const isProfileField = (x: unknown): x is ProfileField => typeof x === 'string' && x in PROFILE_FIELDS;

/** changed: 이미 있는 정보가 바뀐 것 (예: 회사원 → 휴직). 기존 줄은 남기고 "변경:"으로 덧붙인다 */
export type ProfileAddition = { field: ProfileField; value: string; changed?: boolean };

const norm = (t: string) => t.replace(/[\s.,·]/g, '').toLowerCase();

/** 덧붙이는 줄 표시: 몇 회기에서 나왔는지, 바뀐 정보면 "변경:" (빈 칸을 처음 채울 때는 표시 없음) */
const tag = (v: string, a: { changed?: boolean }, session?: number, first = false) =>
  first && !a.changed ? v : `${a.changed ? '변경: ' : ''}${v}${session ? ` (${session}회기)` : ''}`;

function addText(cur: string | undefined, a: ProfileAddition, session?: number): string | undefined {
  const v = a.value.trim();
  if (!v) return undefined;
  if (!cur?.trim()) return tag(v, a, session, true);
  if (norm(cur).includes(norm(v))) return undefined;
  return `${cur.trimEnd()}\n${tag(v, a, session)}`;
}

function addList(cur: string[] | undefined, a: ProfileAddition, session?: number): string[] | undefined {
  const v = a.value.trim();
  if (!v) return undefined;
  const list = cur ?? [];
  if (list.some((x) => norm(x) === norm(v) || norm(x).includes(norm(v)))) return undefined;
  return [...list, tag(v, a, session, !list.length)];
}

/** 덧붙일 patch만 계산한다 (바뀌는 칸만 담김). 같은 칸에 여러 개가 와도 순서대로 누적 */
export function mergeProfile(
  client: Pick<Client, ProfileFieldsOf<'client'>>,
  intake: Pick<IntakeSummary, ProfileFieldsOf<'intake'>> | null,
  additions: ProfileAddition[],
  /** 덧붙이는 줄에 붙일 회기 번호 */
  sessionNumber?: number,
): { client: Partial<Client>; intake: Partial<IntakeSummary>; applied: ProfileAddition[] } {
  const c: Record<string, unknown> = {};
  const i: Record<string, unknown> = {};
  const applied: ProfileAddition[] = [];
  for (const a of additions) {
    const spec = PROFILE_FIELDS[a.field];
    const bag = spec.target === 'client' ? c : i;
    const base = (spec.target === 'client' ? client : (intake ?? {})) as Record<string, unknown>;
    const cur = (a.field in bag ? bag[a.field] : base[a.field]) as never;
    const next = spec.kind === 'list' ? addList(cur, a, sessionNumber) : addText(cur, a, sessionNumber);
    if (next === undefined) continue;
    bag[a.field] = next;
    applied.push(a);
  }
  return { client: c as Partial<Client>, intake: i as Partial<IntakeSummary>, applied };
}

type ProfileFieldsOf<T extends 'client' | 'intake'> = {
  [K in ProfileField]: (typeof PROFILE_FIELDS)[K]['target'] extends T ? K : never;
}[ProfileField];
